import { useEffect, useState } from 'react';
import { CalendarPlus, ShieldAlert } from 'lucide-react';
import type { Me } from '../api/auth';
import { calendarApi } from '../api/calendar';
import type { CalendarSubscriptionStatus } from '../api/calendar';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CopyLink } from '../ui/CopyLink';

/**
 * Subscribing a phone's own calendar to every unit you belong to.
 *
 * Not under a unit, deliberately: one feed covers every unit a person touches, because a parent
 * with a Scout in the troop and a Cub in the pack wants one subscription rather than two — and the
 * guardian relationship that gives them standing in both is the one fact in this model that
 * crosses unit boundaries.
 *
 * The URL is shown once, by the reply that created it. There is no way to read it back, so this
 * screen offers "replace it" rather than "show it again" — which is also exactly the button
 * somebody wants after losing a phone.
 */
export function CalendarSubscriptionScreen({
  me,
  onNavigate,
}: {
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [status, setStatus] = useState<CalendarSubscriptionStatus | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    calendarApi
      .status()
      .then((s) => live && setStatus(s))
      .catch(() => live && setError('Could not check your calendar subscription.'));
    return () => {
      live = false;
    };
  }, []);

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      const issued = await calendarApi.issue();
      setUrl(issued.url);
      setStatus({ active: true, issuedAt: issued.issuedAt });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the feed.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      await calendarApi.revoke();
      setUrl(null);
      setStatus({ active: false, issuedAt: null });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not turn the feed off.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              <CalendarPlus size={14} aria-hidden="true" />
              Your calendar
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-3)' }}
            >
              In your own calendar
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '52ch' }}
            >
              A private link you paste into Google Calendar, Apple Calendar or Outlook. It carries
              every unit you belong to — including the ones you are in as a parent — and updates
              itself.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {url && (
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <CopyLink url={url} label="Your feed address" />
                <p
                  className="t-caption inline-flex items-start gap-2"
                  style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-3)' }}
                >
                  <ShieldAlert
                    size={14}
                    aria-hidden="true"
                    style={{ marginTop: '0.15em', flexShrink: 0 }}
                  />
                  <span>
                    Copy it now — this is the only time it is shown. Anyone holding this link can
                    read your units&rsquo; calendars, so treat it like a password. Lost it? Replace
                    it below; the old one stops working immediately.
                  </span>
                </p>
              </div>
            )}

            {status && (
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" onClick={() => void issue()} disabledReason={busy ? 'Working…' : undefined}>
                  <CalendarPlus size={16} aria-hidden="true" />
                  {status.active ? 'Replace the link' : 'Create my calendar link'}
                </Button>

                {status.active && (
                  <Button variant="secondary" onClick={() => void revoke()} disabledReason={busy ? 'Working…' : undefined}>
                    Turn it off
                  </Button>
                )}
              </div>
            )}

            {status?.active && !url && (
              <p
                className="t-body"
                style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-4)', maxWidth: '52ch' }}
              >
                You already have a feed. Its address is not shown again — replacing it gives you a
                new one and stops the old one working, which is what you want if a phone went
                missing.
              </p>
            )}

            <p
              className="t-caption"
              style={{ color: 'var(--color-text-muted)', marginTop: 'var(--space-6)', maxWidth: '52ch' }}
            >
              Calendar apps cache these feeds for a day or two whatever we do, so a time that
              changes will reach you here first. This app is the answer; your calendar is a
              convenience.
            </p>
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}
